import { INotificationMessage } from "@fl/shared/src/types/messages";
import { CondominioAttributes } from "@fl/shared/src/types/models/superlogica/condominios";
import { CertificadosPerCondsAttributes } from "@fl/shared/src/types/models/suporte_fl/certificados";
import {
  IDataExcel,
  IDataPDF,
  IExcelColumn,
} from "@fl/shared/src/types/reports";
import {
  ITableDataAction,
  ITableDataColumns,
} from "@fl/shared/src/types/tables";
import moment from "moment";
import { TableCell } from "pdfmake/interfaces";
import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { BsCircleFill } from "react-icons/bs";
import { FaEdit, FaRegEdit } from "react-icons/fa";
import ButtonGenerateReport from "../../../../components/generateReport";
import { ExportDatas } from "../../../../components/generateReport/exportData";
import HiddenText from "../../../../components/hiddenText";
import CadastroCondominioModal from "../../../../components/modals/condominios/cadastros";
import TableData from "../../../../components/tables";
import { useNotificationContext } from "../../../../context/notifications";
import ApiCondominios from "../../../../services/suporte/auth/condominios";
import SortFunctions from "../../../../utils/sortArrays";
import {
  formatAddress,
  formatDiasMalote,
} from "../../../../utils/stringsFormats";
import StringsFuncs from "../../../../utils/stringsFuncs";
import DefaultBasePage from "../../../default/base";

export interface CondominioData extends CondominioAttributes {
  Certificados: CertificadosPerCondsAttributes[];
}

const CondominiosCadastrosPrivatePage = () => {
  const columnsTable: ITableDataColumns[] = [
    {
      columnKey: "id_condominio_cond",
      label: "#ID",
      pk: true,
      showColumn: false,
      alignText: "text-center",
    },
    { columnKey: "st_label_cond", label: "Código", alignText: "text-center" },
    {
      columnKey: "st_nome_cond",
      label: "Condomínio",
      alignText: "text-start",
    },
    {
      columnKey: "status",
      label: "Situação",
      alignText: "text-center",
      render: (item: CondominioData) => {
        if (item.fl_ativo_cond === 1) {
          return (
            <BsCircleFill size={13} color="green" title="Condomínio Ativo" />
          );
        } else {
          return (
            <BsCircleFill size={13} color="red" title="Condomínio Inativo" />
          );
        }
      },
      //disableSort: true,
      sortValue: (item: CondominioData) => item.fl_ativo_cond,
    },
    {
      columnKey: "Endereco",
      label: "Endereço",
      alignText: "text-justify",
      render: (item: CondominioData) => {
        return formatAddress(
          item.st_endereco_cond,
          item.st_numeroendereco_cond,
          item.st_complemento_cond,
          item.st_cep_cond,
          item.st_bairro_cond,
          item.st_cidade_cond
        );
      },
      sortValue: (item: CondominioData) => {
        return item.st_endereco_cond?.toLowerCase() || "";
      },
    },
    {
      columnKey: "data_inicio",
      label: "Data de Início",
      alignText: "text-center",
      render: (item: CondominioData) => {
        return item.data_inicio
          ? moment(item.data_inicio).format("DD/MM/YYYY")
          : "-";
      },
    },
    {
      columnKey: "malotes",
      label: "Dias de Malotes",
      alignText: "text-center",
      render: (item: CondominioData) => {
        return formatDiasMalote(item.malotes);
      },
    },
    {
      columnKey: "prazo_edital",
      label: "Prazo Edital",
      alignText: "text-center",
      render: (item: CondominioData) => {
        return item.prazo_edital
          ? `${item.prazo_edital} dias ${item.prazo_edital_dias_uteis ? "úteis" : "corridos"}`
          : "-";
      },
    },
    {
      columnKey: "cartorio",
      label: "Cartório",
      alignText: "text-center",
      render: (item: CondominioData) => {
        return item.cartorio ? `${item.cartorio}` : "-";
      },
    },
    {
      columnKey: "login_web",
      label: "Senha Web",
      alignText: "text-center",
      render: (item: CondominioData) => {
        return item.login_web ? (
          <div>
            <div>{item.login_web}</div>
            <div>
              <HiddenText text={item.senha_web || ""} />
            </div>
          </div>
        ) : (
          "-"
        );
      },
    },
    {
      columnKey: "Certificado", // chave que referencia o array
      label: "Certificado",
      alignText: "text-center",
      render: (item: CondominioData) => {
        const certificados = item.Certificados || [];
        const cert = certificados
          .sort((a, b) =>
            new SortFunctions(a.createdAt, b.createdAt).sortDateDesc()
          )
          .find((item) => moment(item.data_expiracao));

        const dias = cert
          ? moment(cert.data_expiracao).diff(moment(), "day")
          : 0;

        return cert ? (
          <div key={cert.id_certificado}>
            <div>Emissão: {moment(cert.data_emissao).format("DD/MM/YYYY")}</div>
            <div>
              Expiração: {moment(cert.data_expiracao).format("DD/MM/YYYY")}
            </div>
            <div
              style={
                dias > 60
                  ? {
                      backgroundColor: "MediumSpringGreen",
                      borderRadius: "10px",
                      padding: "0px 3px",
                    }
                  : dias > 30
                    ? {
                        backgroundColor: "Gold",
                        borderRadius: "10px",
                        padding: "0px 3px",
                      }
                    : dias >= 0
                      ? {
                          backgroundColor: "LightCoral",
                          borderRadius: "10px",
                          padding: "0px 3px",
                        }
                      : {
                          backgroundColor: "red",
                          borderRadius: "10px",
                          padding: "0px 3px",
                          color: "yellow",
                        }
              }
            >
              {dias <= 0
                ? `Vencido há ${Math.abs(dias)} dia(s)`
                : `Vence em: ${dias} dia(s)`}
            </div>
          </div>
        ) : (
          <div
            style={{
              backgroundColor: "Plum",
              borderRadius: "10px",
              padding: "0px 3px",
            }}
          >
            Nenhum certificado válido
          </div>
        );
      },
      sortValue: (item: CondominioData) => {
        const certificados = item.Certificados || [];
        const cert = certificados.find((item) => moment(item.data_expiracao));

        const dias = cert
          ? moment(cert.data_expiracao).diff(moment(), "day")
          : -9999999999;
        return dias;
      },
    },
    {
      columnKey: "obs",
      label: "Observações",
      alignText: "text-justify",
      render: (item: CondominioData) => {
        return item.obs;
      },
      disableSort: true,
    },
    {
      columnKey: "{actions}",
      label: "Ação",
      action: true,
      actionColumnIcons: true,
      alignText: "text-center",
      actionButtons: (item: CondominioData) => {
        var buttons: ITableDataAction[] = [
          {
            label: `Editar Dados Complementares (${new StringsFuncs(item.st_nome_cond).cammelCase()})`,
            icon: <FaRegEdit size={20} />,
            iconHover: <FaEdit size={20} />,
            hover: false,
            action: (i: CondominioData) => {
              setSelectedCadastro(i);
              setShowModalCadastro(true);
            },
          },
        ];

        return [...buttons];
      },
    },
  ];

  const { addNotification } = useNotificationContext();

  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<INotificationMessage[]>([]);
  const [condominios, setCondominios] = useState<CondominioData[]>([]);
  const [condominiosFiltered, setCondominiosFiltered] = useState<
    CondominioData[]
  >([]); //Seria necessário para usar o FilterComp?
  const [listActivesOnly, setListActivesOnly] = useState(true);
  const [showModalCadastro, setShowModalCadastro] = useState(false);
  const [selectedCadastro, setSelectedCadastro] =
    useState<CondominioData | null>(null);

  useEffect(() => {
    const loadConds = async () => {
      const result = await new ApiCondominios().listWithCerts(
        `?inativos=${listActivesOnly ? "0" : "1"}`
      );

      if (result.success) {
        const certs: CondominioData[] = result.data;
        setCondominios(
          certs.sort((a, b) =>
            new SortFunctions(
              parseFloat(a.st_label_cond || "0"),
              parseFloat(b.st_label_cond || "0")
            ).sortNumberAsc()
          )
        );
      } else {
        if (result.message) setMessages(result.message);
      }
    };

    loadConds();
  }, [listActivesOnly]);

  useEffect(() => {
    setCondominiosFiltered(condominios);
  }, [condominios]);

  const onResultCadastro = async (data: CondominioData | null) => {
    try {
      setLoading(true);
      if (data) {
        const { Certificados, ...dadosCondominios } = data;
        const result = await new ApiCondominios({
          data: dadosCondominios,
        }).update(data.id_condominio_cond?.toString() || "");
        if (result.success) {
          setCondominios((prevCondominios) =>
            prevCondominios.map((item) =>
              item.id_condominio_cond === result.data.id_condominio_cond
                ? { ...item, ...result.data, Certificados: item.Certificados }
                : item
            )
          );
        }

        if (result.message) addNotification(result.message[0]);
      }
    } finally {
      setLoading(false);
      setShowModalCadastro(false);
      setSelectedCadastro(null);
    }
  };

  const getExportDataExcel = (): IDataExcel => {
    const columns: IExcelColumn[] = [
      ExportDatas.col("#", 10, "center"),
      ExportDatas.col("ID Condomínio", 15, "center"),
      ExportDatas.col("Label", 10, "center"),
      ExportDatas.col("Condomínio", 70, "left"),
      ExportDatas.col("Situação (Condomínio)", 15, "center"),
      ExportDatas.col("Endereço", 70, "left"),
      ExportDatas.col("Data de Início", 20, "center", "DD/MM/YYYY"),
      ExportDatas.col("Dias de Malotes", 20, "center", "0"),
      ExportDatas.col("Prazo Edital", 70, "left"),
      ExportDatas.col("Cartório", 70, "left"),
      ExportDatas.col("Senha Web", 70, "left"),
      ExportDatas.col("Situação (Certificado)", 15, "center"),
      ExportDatas.col("Prazo Restante (dias)", 12, "center"),
      ExportDatas.col("Observações", 100, "left"),
    ];

    const rows: any[][] = condominios.map((cond, index) => {
      const address = formatAddress(
        cond.st_endereco_cond,
        cond.st_numeroendereco_cond,
        cond.st_complemento_cond,
        cond.st_cep_cond,
        cond.st_bairro_cond,
        cond.st_cidade_cond
      );

      const cert = [...(cond.Certificados || [])].sort((a, b) =>
        new SortFunctions(a.data_expiracao, b.data_expiracao).sortDateDesc()
      )[0];

      const dias = cert?.data_expiracao
        ? moment(cert.data_expiracao).diff(moment(), "days")
        : null;

      const statusCertificado =
        dias === null ? "Sem certificado" : dias > 0 ? "Válido" : "Expirado";

      return [
        index + 1,
        cond.id_condominio_cond,
        cond.st_label_cond,
        cond.st_nome_cond,
        cond.fl_ativo_cond === 1 ? "Ativo" : "Inativo",
        address,
        cond.data_inicio ? moment(cond.data_inicio).format("DD/MM/YYYY") : "",
        formatDiasMalote(cond.malotes),
        cond.prazo_edital
          ? `${cond.prazo_edital} dias ${cond.prazo_edital_dias_uteis ? "úteis" : "corridos"}`
          : "",
        cond.cartorio ?? "",
        cond.senha_web ?? "",
        statusCertificado,
        dias ?? "",
        cond.obs ?? "",
      ];
    });

    return ExportDatas.generateExcel({
      title: "Relatório de Condomínios",
      columns,
      rows,
      orientation: "landscape",
      margins: {
        left: 0.7,
        right: 0.7,
        top: 0.75,
        bottom: 0.75,
        header: 0.3,
        footer: 0.3,
      },
    });
  };

  const buildExportDataPDF = (): IDataPDF => {
    const columns: TableCell[] = [
      { text: "#", alignment: "center", bold: true },
      { text: "ID Condomínio", alignment: "center", bold: true },
      { text: "Label", alignment: "center", bold: true },
      { text: "Condomínio", alignment: "center", bold: true },
      { text: "Situação (Condomínio)", alignment: "center", bold: true },
      { text: "Endereço", alignment: "center", bold: true },
      { text: "Data de Início", alignment: "center", bold: true },
      { text: "Dias de Malotes", alignment: "center", bold: true },
      { text: "Prazo Edital", alignment: "center", bold: true },
      { text: "Cartório", alignment: "center", bold: true },
      { text: "Senha Web", alignment: "center", bold: true },
      { text: "Situação (Certificado)", alignment: "center", bold: true },
      { text: "Prazo Restante (dias)", alignment: "center", bold: true },
      { text: "Observações", alignment: "center", bold: true },
    ];

    const rows: TableCell[][] = condominios.map((cond, index) => {
      const address = formatAddress(
        cond.st_endereco_cond,
        cond.st_numeroendereco_cond,
        cond.st_complemento_cond,
        cond.st_cep_cond,
        cond.st_bairro_cond,
        cond.st_cidade_cond
      );

      const cert = [...(cond.Certificados || [])].sort((a, b) =>
        new SortFunctions(a.data_expiracao, b.data_expiracao).sortDateDesc()
      )[0];

      const dias = cert?.data_expiracao
        ? moment(cert.data_expiracao).diff(moment(), "days")
        : null;

      const statusCertificado =
        dias === null ? "Sem certificado" : dias > 0 ? "Válido" : "Expirado";

      return [
        { text: index + 1, alignment: "center" },
        { text: cond.id_condominio_cond, alignment: "center" },
        { text: cond.st_label_cond, alignment: "center" },
        cond.st_nome_cond || "",
        {
          text: cond.fl_ativo_cond === 1 ? "Ativo" : "Inativo",
          alignment: "center",
        },
        address,
        {
          text: cond.data_inicio
            ? moment(cond.data_inicio).format("DD/MM/YYYY")
            : "",
          alignment: "center",
        },
        { text: formatDiasMalote(cond.malotes), alignment: "center" },
        {
          text: cond.prazo_edital
            ? `${cond.prazo_edital} dias ${
                cond.prazo_edital_dias_uteis ? "úteis" : "corridos"
              }`
            : "",
          alignment: "left",
        },
        { text: cond.cartorio ?? "", alignment: "left" },
        { text: cond.senha_web ?? "", alignment: "left" },
        { text: statusCertificado, alignment: "center" },
        { text: dias ?? "", alignment: "center" },
        { text: cond.obs ?? "", alignment: "left" },
      ];
    });

    return ExportDatas.generatePDF({
      title: "Relatório de Condomínios",
      columns,
      rows,
      orientation: "landscape",
      pageSize: "A4",
      margins: [40, 60, 40, 60],
      fontSize: 6,
      headerRows: 1,
      zebraStripe: true,
    });
  };

  return (
    <DefaultBasePage
      title="Cadastro dos Condomínios"
      subtitle={`Listagem: ${condominiosFiltered.length} condomínio(s)`}
      filterComp={true}
      dataToFilter={condominios}
      setFilteredData={setCondominiosFiltered}
      loading={loading}
      message={messages}
    >
      <div className="text-justify">
        <Form.Check
          className="my-2"
          type="switch"
          checked={listActivesOnly}
          label={
            listActivesOnly
              ? "Somente condomínios ativos"
              : "Condomínios ativos e inativos"
          }
          onChange={() => setListActivesOnly(!listActivesOnly)}
        />

        <TableData
          key={JSON.stringify(condominiosFiltered)}
          columns={columnsTable}
          data={condominiosFiltered}
        />

        <CadastroCondominioModal
          showModal={showModalCadastro}
          data={selectedCadastro}
          onResultCadastro={onResultCadastro}
        />

        <ButtonGenerateReport
          reportName="Cadastro de Condomínios"
          getExportDataExcel={getExportDataExcel}
          getExportDataPDF={buildExportDataPDF}
          setMessagesExport={setMessages}
          setLoading={setLoading}
        />
      </div>
    </DefaultBasePage>
  );
};

export default CondominiosCadastrosPrivatePage;
